<script setup>
import { useAxios } from '@/hooks/useAxios';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
// Props 정의
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    originalVal: {
        type: String,
        default: ''
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const isReadonly = computed(() => props.readonly);
const inputValue = ref(props.modelValue);
const ValidationMsg = ref('');
const RightMsg = ref('');
const errMessage = ref(props.errmsg);

const verificationCode = ref('');
const verification = ref(false);
const isCodeRequested = ref(false);
const countdown = ref(300); // 5분 = 300초
const countdownTimer = ref(null);

onMounted(async () => {
    // await Promise.all([searchDriver()]);
    // nextTick(() => {
    //     filteredOptions.value = driverData.value;
    // });
});

watch(
    () => props.modelValue,
    (newVal) => {
        if (props.upper) {
            newVal = newVal.toUpperCase();
        }

        inputValue.value = newVal || '';
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const formatPhoneNumber = (value) => {
    if (value.length > 0 && value[0] !== '0') {
        ValidationMsg.value = '올바른 전화번호 형식이 아닙니다. ( 0부터 입력 )';
        return '';
    }

    if (!/^[0-9.-]*$/.test(value)) {
        ValidationMsg.value = value.substring(value.length - 1) + ' 는 올바른 전화번호 형식이 아닙니다.';
    }

    value = value.replace(/[^0-9]/g, '');

    if (value.startsWith('010')) {
        if (value.length <= 3) return value;
        if (value.length <= 7) return value.slice(0, 3) + '-' + value.slice(3);
        return value.slice(0, 3) + '-' + value.slice(3, 7) + '-' + value.slice(7, 11);
    }

    if (value.startsWith('0')) {
        if (value.length <= 3) return value;
        if (value.length <= 6) return value.slice(0, 3) + '-' + value.slice(3);
        if (value.length <= 10) return value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6);
        return value.slice(0, 3) + '-' + value.slice(3, 7) + '-' + value.slice(7, 11);
    }

    return value;
};

const onInput2 = (event) => {
    let value = event.target.value;
    value = value.replace(/[^0-9]/g, '');

    if (value.length > 6) {
        value = value.slice(0, 6);
    }

    verificationCode.value = value;
    ValidationMsg.value = '';
    errMessage.value = '';
};

const onInput = (event) => {
    const oldValue = event.target.value;
    const oldLength = oldValue.length;
    let cursorPosition = event.target.selectionStart;
    let value = event.target.value;
    let validationResult = { isValid: true, message: '' };
    ValidationMsg.value = '';
    errMessage.value = '';

    if (value.length > props.maxlength) {
        value = value.slice(0, props.maxlength);
    }

    const beforeFormatLength = value.length;

    const formattedValue = formatPhoneNumber(value);
    const hyphenPositions = [];
    for (let i = 0; i < formattedValue.length; i++) {
        if (formattedValue[i] === '-') {
            hyphenPositions.push(i);
        }
    }
    // console.log(oldLength, cursorPosition, formattedValue, hyphenPositions);
    if (hyphenPositions.length > 0 && cursorPosition === hyphenPositions[0] + 1) {
        cursorPosition = cursorPosition + 1;
    }

    if (hyphenPositions.length > 1 && cursorPosition === hyphenPositions[1] + 1) {
        cursorPosition = cursorPosition + 1;
    }

    value = formattedValue;

    const afterFormatLength = value.length;
    const lengthDiff = afterFormatLength - beforeFormatLength;

    // 커서가 값의 길이를 초과하지 않도록 보장
    cursorPosition = Math.min(cursorPosition, afterFormatLength);

    if (typeof props.validationCheck === 'function') {
        try {
            validationResult = props.validationCheck(value) || validationResult;
        } catch (error) {
            console.error('Validation check error:', error);
        }
    }

    ValidationMsg.value = validationResult.isValid ? ValidationMsg.value : validationResult.message;
    emitUpdate(value, 'input');

    nextTick(() => {
        event.target.value = value;
        event.target.setSelectionRange(cursorPosition, cursorPosition);
    });
};

const emitUpdate = (value) => {
    inputValue.value = value;
    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        value: value
    });
};

const startCountdown = () => {
    verification.value = false;
    countdown.value = 300;
    clearInterval(countdownTimer.value);
    countdownTimer.value = setInterval(() => {
        countdown.value--;
        if (countdown.value <= 0) {
            clearInterval(countdownTimer.value);
            isCodeRequested.value = false;
            RightMsg.value = '인증번호를 요청하세요.';
            ValidationMsg.value = '';
            errMessage.value = '';
        }
    }, 1000);
};

const formattedCountdown = computed(() => {
    const minutes = Math.floor(countdown.value / 60);
    const seconds = countdown.value % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
});

const requestNumber = async () => {
    if (inputValue.value.length < 12 || inputValue.value.length > 13) {
        ValidationMsg.value = '올바른 전화번호를 입력해주세요.';
        return;
    }
    isCodeRequested.value = true;
    RightMsg.value = '인증번호를 입력하세요.';
    ValidationMsg.value = '';
    errMessage.value = '';

    clearInput2();
    try {
        const url = `/api/customer/signup/verification`;
        const config = {
            method: 'post'
        };
        const { execute, setHeader, data } = useAxios(url, config, {
            immediate: false,
            onSuccess(response) {
                //console.log(response.data.data);
                if (!response.data.success) {
                    console.log('인증번호 전송 실패');
                } else {
                    startCountdown();
                    RightMsg.value = '인증번호가 발송되었습니다.';
                }
            },
            onError(err) {
                if (!err.response.data.success) {
                    ValidationMsg.value = '인증번호 요청 실패하였습니다.';
                }
                // console.error('Error:', err.message);
            }
        });
        setHeader('Content-Type', 'application/json');
        execute({
            phone: inputValue.value
        });
    } catch (error) {
        console.error('인증번호 요청 중 오류 발생:', error);
        ValidationMsg.value = '인증번호 요청 중 오류가 발생했습니다.';
    }
};

const resetVerifyCode = () => {
    clearInterval(countdownTimer.value);
    countdown.value = 0;
    ValidationMsg.value = '';
    RightMsg.value = '';
    errMessage.value = '';
    verification.value = false;
    isCodeRequested.value = false;
};

const verifyCode = () => {
    if (verificationCode.value === '' || verificationCode.value.length !== 6) ValidationMsg.value = '올바른 인증번호를 입력하세요.';
    try {
        const url = `/api/customer/signup/verification`;
        const config = {
            method: 'put'
        };
        const { execute, setHeader, data } = useAxios(url, config, {
            immediate: false,
            onSuccess(response) {
                //console.log(response.data.data);
                if (!response.data.success) {
                    console.log('인증번호 인증 오류');
                } else {
                    RightMsg.value = '인증되었습니다.';
                    clearInterval(countdownTimer.value);
                    countdown.value = 0;
                    verification.value = true;
                }
            },
            onError(err) {
                if (!err.response.data.success) {
                    ValidationMsg.value = '인증번호를 확인하세요.';
                }
                // console.error('Error:', err.message);
            }
        });
        setHeader('Content-Type', 'application/json');
        execute({
            phone: inputValue.value,
            verification_code: verificationCode.value
        });
    } catch (error) {
        console.error('인증번호 확인 중 오류 발생:', error);
        ValidationMsg.value = '인증번호 확인 중 오류가 발생했습니다.';
    }
};

const clearInput = () => {
    emitUpdate('', 'input');
};

const clearInput2 = () => {
    verificationCode.value = '';
};

const showRequestButton = computed(() => {
    const originalVal = props.originalVal ? props.originalVal.replace(/-/g, '') : '';
    const inputVal = inputValue.value ? inputValue.value.replace(/-/g, '') : '';
    return inputValue.value && !verification.value && originalVal !== inputVal;
});

defineExpose({
    verification
});
</script>

<template>
    <div
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
            <span v-if="!ValidationMsg && RightMsg" class="right-message">{{ RightMsg }}</span>
        </label>

        <div class="relative flex flex-col items-center w-full">
            <div class="relative flex items-center w-full h-12">
                <input
                    v-if="visible"
                    type="text"
                    :id="inputId"
                    v-model="inputValue"
                    class="block w-full p-3 ps-5 input-font text-gray-900 border border-gray-300 rounded-lg bg-gray-60 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    :class="{
                        'pr-24': button,
                        'error-border': ValidationMsg
                    }"
                    required
                    placeholder=" - 제외 숫자만 입력하세요."
                    :required="required"
                    :readonly="verification"
                    @input="onInput"
                />

                <div class="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center">
                    <button v-if="inputValue && !verification" @click="clearInput" class="clear-button mr-[calc(6.5rem)]" :class="{ 'mr-[1rem]': !showRequestButton }">&times;</button>
                    <button v-if="showRequestButton" @click="requestNumber" class="button-style">인증번호 요청</button>
                </div>
            </div>
            <div v-if="isCodeRequested" class="relative flex items-center w-full h-12 mt-2">
                <input
                    v-model="verificationCode"
                    type="text"
                    class="block w-full p-3 ps-5 input-font text-gray-900 border border-gray-300 rounded-lg bg-gray-60 focus:ring-blue-500 focus:border-blue-500"
                    :class="{ 'pr-24': true, 'error-border': ValidationMsg }"
                    :placeholder="`인증번호 6자리 입력`"
                    maxlength="6"
                    :readonly="verification"
                    @input="onInput2"
                />
                <div class="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center pr-2">
                    <span v-if="!verification" class="countdown mr-[calc(8rem)]">{{ formattedCountdown }}</span>
                    <button v-if="!verification && verificationCode && !isReadonly" @click="clearInput2" class="clear-button mr-[calc(6.5rem)]">&times;</button>
                    <button v-if="!verification" @click="verifyCode" class="button-style">확인</button>
                    <button v-if="verification" @click="resetVerifyCode" class="button-style">초기화</button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus {
    border-color: #0038ea;
    outline: none;
}

.text-right {
    text-align: right;
    padding-right: 6rem;
}

.error-border {
    border: 1px solid red !important;
}
.custom-input.p-inputtext {
    padding: 0.75rem; /* 기본 높이 */
    border: 1px solid #d1d5db; /* 기본 테두리 색상 */
    border-radius: 0.5rem; /* 기본 border-radius */
    /* transition:
        border-color 0.3s ease,
        box-shadow 0.3s ease,
        height 0.3s ease; 애니메이션 */
}

.custom-input.p-inputtext:focus {
    border-color: #0038ea !important;
    /* border-radius: 0.5rem !important;*/
    /* height: 2.75rem !important; 포커스 시 높이 증가 */
    /* outline: none !important; 포커스 시 기본 outline 제거 */
}

.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    transition: color 0.3s ease;
    border-radius: 0.5rem;
    background-color: #f1f1f1;
    color: #4b5563;
    align-items: center;
    position: absolute;
    right: 0.5rem;
    width: 5.5rem;
    height: 1.8rem;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.right-message {
    color: green;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.countdown {
    font-family: 'SCoreDream500';
    font-size: 0.875rem;
    color: #0038ea;
    display: inline-block;
    margin-top: 0.1rem;
}

.unit-text {
    font-family: 'SCoreDream500';
    font-size: 0.8rem;
    color: #4b5563;
    height: 100%;
    display: flex;
    align-items: center;
    position: absolute;
    padding-top: 0.2rem;
    right: 2.1rem;
    width: 2rem;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 0.9rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

.search-input {
    width: 100%;
    padding: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
}
</style>
